import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Button,
  IndexTable,
  LegacyCard,
  Page,
  Spinner,
  Text,
  Pagination,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import { formatFriendlyDate, formatPlate } from "../../utility/funcs";
import InsuranceStatusBadge from "./InsuranceStatusBadge";
import * as XLSX from "xlsx/xlsx.mjs";
import {
  applyHeaderFormats,
  moneyHeaders,
  toExcelSerial,
  DATE_ONLY_FMT,
} from "../../utility/xlsxExport";

const PERIODICITY_LABELS = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const numOrBlank = (v) =>
  v === undefined || v === null || v === "" ? "" : Number(v);

function AllInsurancePolicies() {
  const navigate = useNavigate();
  const { hasPermission } = UserAuth();
  const [searchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);

  // Filters
  const filterStatus = searchParams.get("status");

  // Data
  const [policies, setPolicies] = useState([]);
  const [total, setTotal] = useState(0);

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicies = async () => {
      setLoading(true);

      const from = (page - 1) * limit;

      // Filter by STATUS
      if (filterStatus) {
        if (filterStatus === "no_insurance") {
          const { data, error } = await supabase.rpc(
            "get_cars_without_active_insurance"
          );
          if (!error) {
            setPolicies(data ?? []);
            setTotal(data?.length ?? 0);
          }
          setLoading(false);
          return;
        }

        if (filterStatus === "expired") {
          const { data, error } = await supabase.rpc(
            "get_expired_insurance_policies"
          );
          if (!error) {
            setPolicies(data ?? []);
            setTotal(data?.length ?? 0);
          }
          setLoading(false);
          return;
        }

        const to = from + limit - 1;
        const { data, error, count } = await supabase
          .from("insurance")
          .select(
            `
          id,
          status,
          validity_from,
          validity_to,
          policy_no,
          is_temporary,
          created_at,
          car(id, plate),
          provider(name)
        `,
            { count: "exact" }
          )
          .eq("status", filterStatus)
          .order("created_at", { ascending: false })
          .range(from, to);

        if (error) console.error(error);
        setPolicies(data ?? []);
        setTotal(count ?? 0);
        setLoading(false);
        return;
      }

      // Default — latest insurance per car (active cars only)
      const { data, error } = await supabase.rpc(
        "get_current_insurance_policies",
        { p_limit: limit, p_offset: from }
      );
      if (error) console.error(error);
      setPolicies(data ?? []);
      setTotal(data?.[0]?.total_count ?? 0);
      setLoading(false);
    };

    fetchPolicies();
  }, [page, filterStatus]);

  // --------------------------------------------
  // TABLE ROWS
  // --------------------------------------------
  const rowMarkup = policies.map((policy, index) => {
    const id = policy.id;

    return (
      <IndexTable.Row
        key={id}
        id={id}
        position={index}
        onClick={() => navigate(`/insurance/${id}`)}
      >
        <IndexTable.Cell>
          <Text tone="subdued">#{id}</Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text>{formatPlate(policy.car?.plate)}</Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text>{policy.provider?.name}</Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text>
            {policy.policy_no || (policy.is_temporary ? "TEMPORAL" : "—")}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text>{formatFriendlyDate(policy.validity_from)}</Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text>{formatFriendlyDate(policy.validity_to)}</Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <InsuranceStatusBadge status={policy.status} />
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  const headings = [
    { title: "ID" },
    { title: "Placa" },
    { title: "Aseguradora" },
    { title: "Póliza" },
    { title: "Desde" },
    { title: "Hasta" },
    { title: "Status" },
  ];

  // --------------------------------------------
  // TABLE COMPONENT
  // --------------------------------------------
  const tableComponent = () => (
    <LegacyCard>
      <IndexTable
        resourceName={{ singular: "Póliza", plural: "Pólizas" }}
        selectable={false}
        itemCount={policies.length}
        headings={headings}
      >
        {rowMarkup}
      </IndexTable>

      <div
        style={{
          padding: "1rem",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Pagination
          hasPrevious={page > 1}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          hasNext={page * limit < total}
          onNext={() => setPage((p) => p + 1)}
        />
      </div>
    </LegacyCard>
  );

  // --------------------------------------------
  // EXPORT EXCEL
  // --------------------------------------------
  const exportAsExcel = async () => {
    setIsExporting(true);
    const { data, error } = await supabase.rpc("get_all_insurance_data");
    if (error) {
      console.error(error);
      setIsExporting(false);
      return;
    }

    const cleaned = data.map((row) => ({
      "No. de Poliza": row.policy_no ?? "",
      Aseguradora: row.insurance_provider ?? "",
      Marca: row.brand ?? "",
      Modelo: row.model ?? "",
      Placa: row.plate ?? "",
      "No. de Serie": row.vin ?? "",
      Año: row.year ?? "",
      "Costo de Adquisicion": row.acquisition_price ?? "",
      "Costo de Adaptaciones": row.equipment_cost ?? "",
      "Prima Neta": row.net_premium ?? "",
      Financiamiento: numOrBlank(row.shared_charges?.Financiamiento),
      "Gasto de Expedición": numOrBlank(
        row.per_payment_charges?.Gasto_de_Expedicion
      ),
      Periodicidad: PERIODICITY_LABELS[row.periodicity] ?? row.periodicity ?? "",
      Emisión: toExcelSerial(row.emission_date) ?? "",
      Desde: toExcelSerial(row.validity_from) ?? "",
      Hasta: toExcelSerial(row.validity_to) ?? "",
      "Otros Datos": JSON.stringify(row.custom_fields) ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(cleaned);
    // Premiums and vehicle costs go in as real numeric cells with a currency
    // format so the columns can be summed; the vigencia dates as date cells.
    applyHeaderFormats(worksheet, {
      ...moneyHeaders([
        "Costo de Adquisicion",
        "Costo de Adaptaciones",
        "Prima Neta",
        "Financiamiento",
        "Gasto de Expedición",
      ]),
      "Emisión": DATE_ONLY_FMT,
      Desde: DATE_ONLY_FMT,
      Hasta: DATE_ONLY_FMT,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pólizas");

    XLSX.writeFile(workbook, "Polizas de Seguro - GoMove.xlsx");
    setIsExporting(false);
  };

  return (
    <AppWrapper>
      <Page
        title="Pólizas de Seguro"
        primaryAction={{
          content: "Crear Póliza",
          url: "/insurance/new",
          disabled: !hasPermission("insurance.create"),
        }}
        secondaryActions={
          <Button onClick={exportAsExcel} loading={isExporting}>
            Exportar Todo
          </Button>
        }
        backAction={{ onAction: () => navigate(-1) }}
      >
        {loading ? <Spinner size="small" /> : tableComponent()}
      </Page>
    </AppWrapper>
  );
}

export default AllInsurancePolicies;
